import { usePageRefreshKey } from '../../../../../context/PageRefreshContext';
import { localDateString } from '../../../../../utils/helpers/localDate';
import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from '@iconify/react';
import { getTariffHeads } from "../../../../../redux/actions/SocietyPortal/TariffHeadAction/TariffHeadAction";
import { getMeterReadings, createBulkMeterReadings, uploadMeterReadings, downloadMeterReadingTemplate, updateMeterReading, deleteMeterReading } from "../../../../../redux/actions/SocietyPortal/MeterReadingAction/MeterReadingAction";
import { selectedSocietyStorer } from "../../../../../utils/webStorer";
import FormSelect from "../../../../../components/child/FormSelect";
import FormButton from "../../../../../components/child/FormButton";
import { PaginationLimit } from "../../../../../components/PaginationLimit";

const MeterReading = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    // Redux state
    const { tariffHeadsData } = useSelector(state => state.TariffHeadReducer);
    const { meterReadingsData, meterReadingsPagination, loading, bulkCreateLoading, uploadLoading, downloadLoading, deleteLoading, updateLoading } = useSelector(state => state.MeterReadingReducer);

    // File input ref
    const fileInputRef = useRef(null);

    // Local state
    const [selectedHead, setSelectedHead] = useState("");
    const [billingMonth, setBillingMonth] = useState("");
    const [billingYear, setBillingYear] = useState("");
    const [readings, setReadings] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    // Outcome of the last Excel upload that had problems: { message, errors: [{ row, flat_no, error }] }
    const [uploadReport, setUploadReport] = useState(null);

    // Get METERED type charge heads
    const meteredHeads = tariffHeadsData?.filter(h => h.calculation_type === 'METERED' && h.status === 1) || [];

    // Month options
    const monthOptions = [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' }
    ];

    // Year options (current year and 2 previous years)
    const currentYear = new Date().getFullYear();
    const yearOptions = [
        { value: currentYear, label: String(currentYear) },
        { value: currentYear - 1, label: String(currentYear - 1) },
        { value: currentYear + 1, label: String(currentYear + 1) }
    ];


    // Fetch charge heads on mount
    useEffect(() => {
        if (societyId) {
            dispatch(getTariffHeads({ society_id: societyId, page: 1, limit: 100 }));
        }
    }, [dispatch, societyId]);

    // Fetch existing readings when head/month/year changes
    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        if (societyId) {
            dispatch(getMeterReadings({
                society_id: societyId,
                head_id: selectedHead || undefined,
                billing_month: billingMonth || undefined,
                billing_year: billingYear || undefined,
                page: currentPage,
                limit: pageSize
            }));
        }
    }, [dispatch, selectedHead, billingMonth, billingYear, societyId, currentPage, pageSize, refreshKey]);

    // Initialize readings from API response
    // status: 0=deleted, 1=active, 2=billed (billed is not editable/deleteable)
    const initializeReadings = useCallback(() => {
        const initialReadings = meterReadingsData?.map(r => ({
            reading_id: r.reading_id,
            head_id: r.head_id,
            flat_id: r.flat_id,
            flat_no: r.flat?.flat_number || '-',
            wing_name: r.flat?.wing?.title || r.flat?.wing?.wing_name || '',
            head_name: r.tariffHead?.head_name || '-',
            billing_month: r.billing_month,
            billing_year: r.billing_year,
            last_reading: parseFloat(r.last_reading) || 0,
            current_reading: r.current_reading || '',
            consumption: parseFloat(r.consumption_units) || 0,
            rate_type: r.rate_type || '',
            custom_rate: parseFloat(r.custom_rate) || 0,
            fixed_charge: parseFloat(r.fixed_charge) || 0,
            factor: parseFloat(r.factor) || 1,
            calculated_amount: parseFloat(r.calculated_amount) || 0,
            reading_date: r.reading_date || '',
            description: r.description || '',
            api_status: r.status, // 0=deleted, 1=active, 2=billed
            is_modified: false
        })) || [];

        // Sort by flat_no
        initialReadings.sort((a, b) => {
            const aNum = parseInt(a.flat_no) || 0;
            const bNum = parseInt(b.flat_no) || 0;
            return aNum - bNum;
        });

        setReadings(initialReadings);
    }, [meterReadingsData]);

    // Initialize readings when data is available
    useEffect(() => {
        if (meterReadingsData?.length > 0) {
            initializeReadings();
        } else {
            setReadings([]);
        }
    }, [meterReadingsData, initializeReadings]);

    // Calculate amount based on consumption, rate, fixed charge, and factor
    const calculateAmount = (consumption, rate, fixedCharge, factor) => {
        return (parseFloat(consumption) || 0) * (parseFloat(rate) || 0) * (parseFloat(factor) || 1) + (parseFloat(fixedCharge) || 0);
    };

    // Handle inline field change
    const handleReadingChange = (readingId, field, value) => {
        setReadings(prev => prev.map(reading => {
            if (reading.reading_id === readingId) {
                const updates = { [field]: value };

                // Get updated values for calculation
                const currentReading = field === 'current_reading' ? parseFloat(value) || 0 : parseFloat(reading.current_reading) || 0;
                const lastReading = parseFloat(reading.last_reading) || 0;
                const consumption = currentReading >= lastReading ? currentReading - lastReading : 0;
                const rate = field === 'custom_rate' ? parseFloat(value) || 0 : parseFloat(reading.custom_rate) || 0;
                const fixedCharge = field === 'fixed_charge' ? parseFloat(value) || 0 : parseFloat(reading.fixed_charge) || 0;
                const factor = field === 'factor' ? parseFloat(value) || 1 : parseFloat(reading.factor) || 1;

                // Update consumption if current_reading changed
                if (field === 'current_reading') {
                    updates.consumption = consumption;
                }

                // Recalculate amount for any of these fields
                if (['current_reading', 'custom_rate', 'fixed_charge', 'factor'].includes(field)) {
                    updates.calculated_amount = calculateAmount(consumption, rate, fixedCharge, factor);
                }

                return {
                    ...reading,
                    ...updates,
                    is_modified: true
                };
            }
            return reading;
        }));
    };

    // Handle update single reading
    const handleUpdateReading = (reading) => {
        dispatch(updateMeterReading({
            id: reading.reading_id,
            last_reading: parseFloat(reading.last_reading) || 0,
            current_reading: parseFloat(reading.current_reading) || 0,
            rate_type: reading.rate_type || 'CUSTOM',
            custom_rate: parseFloat(reading.custom_rate) || 0,
            fixed_charge: parseFloat(reading.fixed_charge) || 0,
            factor: parseFloat(reading.factor) || 1,
            reading_date: reading.reading_date || localDateString(),
            description: reading.description || '',
            onSuccess: () => {
                refreshReadings();
            }
        }));
    };

    // Handle delete reading
    const handleDeleteReading = (readingId) => {
        if (window.confirm('Are you sure you want to delete this reading?')) {
            dispatch(deleteMeterReading({
                id: readingId,
                onSuccess: () => {
                    refreshReadings();
                }
            }));
        }
    };

    // Refresh readings helper
    const refreshReadings = () => {
        dispatch(getMeterReadings({
            society_id: societyId,
            head_id: selectedHead || undefined,
            billing_month: billingMonth || undefined,
            billing_year: billingYear || undefined,
            page: currentPage,
            limit: pageSize
        }));
    };

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleBulkSubmit = () => {
        const readingsToSubmit = readings
            .filter(r => r.current_reading !== '' && r.is_modified)
            .map(r => ({
                society_id: societyId,
                head_id: r.head_id,
                flat_id: r.flat_id,
                billing_month: r.billing_month,
                billing_year: r.billing_year,
                last_reading: parseFloat(r.last_reading) || 0,
                current_reading: parseFloat(r.current_reading) || 0,
                rate_type: r.rate_type || 'CUSTOM',
                custom_rate: parseFloat(r.custom_rate) || 0,
                fixed_charge: parseFloat(r.fixed_charge) || 0,
                factor: parseFloat(r.factor) || 1,
                reading_date: r.reading_date || localDateString(),
                description: r.description || ''
            }));

        if (readingsToSubmit.length === 0) {
            return;
        }

        dispatch(createBulkMeterReadings({
            readings: readingsToSubmit,
            onSuccess: () => {
                refreshReadings();
            }
        }));
    };

    // Handle Excel file upload
    // Note: head_id, billing_month, billing_year come from Excel columns
    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Reset the input straight away (the File object stays usable). It was
        // only cleared on success, so after a failed upload re-picking the
        // corrected file of the same name did nothing.
        event.target.value = '';
        setUploadReport(null);

        dispatch(uploadMeterReadings({
            file,
            society_id: societyId,
            onSuccess: (data) => {
                // Some rows can be rejected even when the upload succeeds
                if (data?.errors?.length) {
                    setUploadReport({
                        message: `${data.failed || data.errors.length} row(s) were not uploaded`,
                        errors: data.errors
                    });
                }
                // Refresh via the shared helper so empty-string month/year get
                // coerced to `undefined` (BE rejects `billing_month=""`, which
                // was surfacing a stray error toast right after a successful
                // upload).
                refreshReadings();
            },
            onError: (report) => setUploadReport(report)
        }));
    };

    // Handle template download
    const handleDownloadTemplate = () => {
        if (!selectedHead) return;

        dispatch(downloadMeterReadingTemplate({
            society_id: societyId,
            head_id: selectedHead
        }));
    };

    // Get status badge based on api_status: 0=deleted, 1=active, 2=billed
    const getStatusBadge = (apiStatus, isModified) => {
        if (isModified) {
            return <span className="badge bg-warning-50 text-warning-600 text-xs">Modified</span>;
        }
        switch (apiStatus) {
            case 2:
                return <span className="badge bg-info-50 text-info-600 text-xs">Billed</span>;
            case 1:
                return <span className="badge bg-success-50 text-success-600 text-xs">Active</span>;
            case 0:
                return <span className="badge bg-danger-50 text-danger-600 text-xs">Deleted</span>;
            default:
                return <span className="badge bg-neutral-100 text-secondary-light text-xs">-</span>;
        }
    };

    // Get month name from number
    const getMonthName = (month) => {
        const months = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return months[month] || '-';
    };

    const pendingCount = readings.filter(r => r.is_modified).length;
    const totalRecords = meterReadingsPagination?.total || 0;

    return (
        <div className="invoice-setup-page">
            {/* Page Header */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-12">
                <div>
                    <h6 className="fw-semibold mb-0 text-xl">Meter Reading Entry</h6>
                    <p className="text-secondary-light text-sm mb-0 mt-1">
                        Enter meter readings for metered charge heads
                    </p>
                </div>
                <div className="d-flex flex-wrap gap-2 meter-actions">
                    {/* Download Template Button */}
                    <FormButton
                        onClick={handleDownloadTemplate}
                        variant="outline-info"
                        className="btn-outline-info"
                        icon="mdi:file-download"
                        isLoading={downloadLoading}
                        disabled={!selectedHead}
                    >
                        Download Template
                    </FormButton>

                    {/* Upload Excel Button */}
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept=".xlsx,.xls"
                        style={{ display: 'none' }}
                    />
                    <FormButton
                        onClick={() => fileInputRef.current?.click()}
                        variant="outline-success"
                        className="btn-outline-success"
                        icon="mdi:file-upload"
                        isLoading={uploadLoading}
                    >
                        Upload Excel
                    </FormButton>

                    {/* Save All Button */}
                    <FormButton
                        onClick={handleBulkSubmit}
                        variant="primary"
                        icon="mdi:content-save-all"
                        isLoading={bulkCreateLoading}
                        disabled={!selectedHead || pendingCount === 0}
                    >
                        Save All ({pendingCount})
                    </FormButton>
                </div>
            </div>

            {/* Excel upload errors - stays on screen until dismissed (the toast alone disappears) */}
            {uploadReport && (
                <div className="card radius-8 border-0 mb-16 bg-danger-50">
                    <div className="card-body p-12">
                        <div className="d-flex align-items-start justify-content-between gap-3">
                            <div className="d-flex align-items-center gap-2">
                                <Icon icon="mdi:alert-circle-outline" className="text-danger-600 text-lg flex-shrink-0" />
                                <strong className="text-danger-600 text-sm">
                                    Excel upload: {uploadReport.message || 'Upload failed'}
                                </strong>
                            </div>
                            <button
                                type="button"
                                className="btn-close flex-shrink-0"
                                aria-label="Dismiss upload errors"
                                onClick={() => setUploadReport(null)}
                            />
                        </div>
                        {uploadReport.errors?.length > 0 && (
                            <ul className="text-xs text-secondary-light mb-0 mt-8 ps-20" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                {uploadReport.errors.map((e, idx) => (
                                    <li key={idx}>
                                        <strong>Row {e.row}</strong>
                                        {e.flat_no ? ` (Flat ${e.wing_code ? `${e.wing_code}-` : ''}${e.flat_no})` : ''}: {e.error}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}

            {/* Info Banner */}
            <div className="card radius-8 border-0 mb-16 bg-primary-50">
                <div className="card-body p-12 d-flex align-items-center gap-3">
                    <div className="w-40-px h-40-px rounded-circle bg-primary-100 d-flex align-items-center justify-content-center flex-shrink-0">
                        <Icon icon="mdi:counter" className="text-primary-600 text-xl" />
                    </div>
                    <p className="text-xs text-secondary-light mb-0">
                        <strong className="text-primary-600">Note:</strong> Select a metered charge head and billing period,
                        then enter current readings for each flat. The system will calculate consumption and amount automatically.
                    </p>
                </div>
            </div>

            {/* Selection Controls */}
            <div className="card radius-12 border-0 shadow-sm mb-16">
                <div className="card-body p-24">
                    <div className="row g-3">
                        {/* Charge Head Selection */}
                        <div className="col-12 col-md-3">
                            <FormSelect
                                label="Charge Head"
                                name="head_id"
                                value={selectedHead}
                                onChange={(e) => setSelectedHead(e.target.value)}
                                options={meteredHeads.map(h => ({
                                    value: h.head_id,
                                    label: `${h.head_name} (${h.head_code})`
                                }))}
                                placeholder="-- Select Charge Head --"
                            />
                        </div>

                        {/* Billing Month */}
                        <div className="col-6 col-md-2">
                            <FormSelect
                                label="Billing Month"
                                name="billing_month"
                                value={billingMonth}
                                onChange={(e) => setBillingMonth(parseInt(e.target.value))}
                                options={monthOptions}
                            />
                        </div>

                        {/* Billing Year */}
                        <div className="col-6 col-md-2">
                            <FormSelect
                                label="Billing Year"
                                name="billing_year"
                                value={billingYear}
                                onChange={(e) => setBillingYear(parseInt(e.target.value))}
                                options={yearOptions}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Readings Table */}
            <div className="card radius-12 border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading || deleteLoading || updateLoading ? (
                        <div className="d-flex justify-content-center align-items-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover mb-0 meter-table">
                                <thead className="bg-neutral-50">
                                    <tr>
                                        <th className="ps-20 py-8 text-secondary-light text-xs fw-semibold">#</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Flat</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Wing</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Head Name</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold text-center">Month</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold text-center">Year</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold text-end">Last Reading</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold text-center" style={{ width: '110px' }}>Current Reading</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold text-end">Consumption</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold text-center">Rate Type</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold text-center" style={{ width: '90px' }}>Rate</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold text-center" style={{ width: '100px' }}>Fixed Charge</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold text-center" style={{ width: '80px' }}>Factor</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold text-end">Amount</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold text-center">Status</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold text-center pe-20">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {readings.length > 0 ? readings.map((reading, index) => {
                                        const isBilled = reading.api_status === 2;
                                        const isEditable = !isBilled;
                                        return (
                                            <tr key={reading.reading_id} className="meter-row">
                                                <td className="meter-cell-index ps-20 py-12 align-middle">
                                                    <span className="text-secondary-light fw-medium">{String(index + 1).padStart(2, '0')}</span>
                                                </td>
                                                <td data-label="Flat" className="meter-cell-flat py-12 align-middle">
                                                    <span className="fw-semibold">{reading.flat_no}</span>
                                                </td>
                                                <td data-label="Wing" className="py-12 align-middle">
                                                    <span className="text-secondary-light">{reading.wing_name || '-'}</span>
                                                </td>
                                                <td data-label="Head Name" className="py-12 align-middle">
                                                    <span className="text-secondary-light">{reading.head_name}</span>
                                                </td>
                                                <td data-label="Month" className="py-12 align-middle text-center">
                                                    <span className="text-secondary-light">{getMonthName(reading.billing_month)}</span>
                                                </td>
                                                <td data-label="Year" className="py-12 align-middle text-center">
                                                    <span className="text-secondary-light">{reading.billing_year || '-'}</span>
                                                </td>
                                                <td data-label="Last Reading" className="py-12 align-middle text-end">
                                                    <span className="fw-medium">{parseFloat(reading.last_reading || 0).toFixed(2)}</span>
                                                </td>
                                                <td data-label="Current Reading" className="py-12 align-middle text-center">
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm text-center"
                                                        value={reading.current_reading}
                                                        onChange={(e) => handleReadingChange(reading.reading_id, 'current_reading', e.target.value)}
                                                        placeholder="0.00"
                                                        step="0.01"
                                                        style={{ width: '100px', margin: '0 auto' }}
                                                        disabled={!isEditable}
                                                    />
                                                </td>
                                                <td data-label="Consumption" className="py-12 align-middle text-end">
                                                    <span className={`fw-semibold ${reading.consumption > 0 ? 'text-primary-600' : 'text-secondary-light'}`}>
                                                        {parseFloat(reading.consumption || 0).toFixed(2)}
                                                    </span>
                                                </td>
                                                <td data-label="Rate Type" className="py-12 align-middle text-center">
                                                    {reading.rate_type ? (
                                                        <span className={`badge ${reading.rate_type === 'SLAB' ? 'bg-info-50 text-info-600' : 'bg-warning-50 text-warning-600'} text-xs`}>
                                                            {reading.rate_type}
                                                        </span>
                                                    ) : '-'}
                                                </td>
                                                <td data-label="Rate" className="py-12 align-middle text-center">
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm text-center"
                                                        value={reading.custom_rate || ''}
                                                        onChange={(e) => handleReadingChange(reading.reading_id, 'custom_rate', e.target.value)}
                                                        placeholder="0.00"
                                                        step="0.01"
                                                        style={{ width: '80px', margin: '0 auto' }}
                                                        disabled={!isEditable}
                                                    />
                                                </td>
                                                <td data-label="Fixed Charge" className="py-12 align-middle text-center">
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm text-center"
                                                        value={reading.fixed_charge || ''}
                                                        onChange={(e) => handleReadingChange(reading.reading_id, 'fixed_charge', e.target.value)}
                                                        placeholder="0.00"
                                                        step="0.01"
                                                        style={{ width: '90px', margin: '0 auto' }}
                                                        disabled={!isEditable}
                                                    />
                                                </td>
                                                <td data-label="Factor" className="py-12 align-middle text-center">
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm text-center"
                                                        value={reading.factor || ''}
                                                        onChange={(e) => handleReadingChange(reading.reading_id, 'factor', e.target.value)}
                                                        placeholder="1.00"
                                                        step="0.01"
                                                        style={{ width: '70px', margin: '0 auto' }}
                                                        disabled={!isEditable}
                                                    />
                                                </td>
                                                <td data-label="Amount" className="py-12 align-middle text-end">
                                                    <span className="fw-semibold text-success-600">
                                                        {reading.calculated_amount ? parseFloat(reading.calculated_amount).toFixed(2) : '-'}
                                                    </span>
                                                </td>
                                                <td className="meter-cell-status py-12 align-middle text-center">
                                                    {getStatusBadge(reading.api_status, reading.is_modified)}
                                                </td>
                                                <td className="meter-cell-action py-12 align-middle text-center pe-20">
                                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                                        {reading.is_modified && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-success-50 text-success-600 p-1"
                                                                onClick={() => handleUpdateReading(reading)}
                                                                title="Save Changes"
                                                            >
                                                                <Icon icon="mdi:content-save" className="text-lg" />
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-danger-50 text-danger-600 p-1"
                                                            onClick={() => handleDeleteReading(reading.reading_id)}
                                                            disabled={isBilled}
                                                            title={isBilled ? "Cannot delete billed reading" : "Delete Reading"}
                                                        >
                                                            <Icon icon="mdi:delete" className="text-lg" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    }) : (
                                        <tr>
                                            <td colSpan={16} className="text-center py-20">
                                                <Icon icon="mdi:file-document-outline" className="text-secondary-light text-3xl mb-2" style={{ opacity: 0.3 }} />
                                                <p className="text-secondary-light text-sm mb-0">No meter readings found</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalRecords > 0 && (
                    <div className="card-footer bg-white border-top px-20 py-8">
                        <PaginationLimit
                            currentPage={currentPage}
                            totalRecords={totalRecords}
                            limit={pageSize}
                            handleLimitChange={(e) => {
                                setPageSize(parseInt(e.target.value));
                                setCurrentPage(1);
                            }}
                            handlePageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>

            {/* Custom Styles */}
            <style>{`
                .w-40-px { width: 40px; }
                .h-40-px { height: 40px; }
                .w-80-px { width: 80px; }
                .h-80-px { height: 80px; }

                .form-control-sm {
                    font-size: 0.875rem;
                    padding: 0.375rem 0.5rem;
                }

                .table > :not(caption) > * > * {
                    border-bottom-color: var(--bs-border-color);
                }

                .meter-table .form-control::placeholder {
                    font-size: inherit !important;
                }

                /* Below md the 16-column table becomes one card per flat. It stays a single
                   table so every input keeps one handler; each cell's data-label stands in
                   for the hidden header. */
                @media (max-width: 767.98px) {
                    .meter-actions {
                        width: 100%;
                    }

                    .meter-actions > .btn {
                        flex: 1 1 auto;
                        padding-inline: 10px !important;
                        white-space: nowrap;
                    }

                    .meter-table,
                    .meter-table tbody {
                        display: block;
                        width: 100%;
                    }

                    .meter-table thead {
                        display: none;
                    }

                    .meter-table tbody > tr:not(.meter-row),
                    .meter-table tbody > tr:not(.meter-row) > td {
                        display: block;
                        width: 100%;
                    }

                    .meter-table .meter-row {
                        display: grid;
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                        gap: 10px 12px;
                        padding: 12px 14px;
                        border-bottom: 1px solid var(--bs-border-color);
                    }

                    .invoice-setup-page .meter-table .meter-row > td {
                        display: block;
                        min-width: 0;
                        padding: 0 !important;
                        border: 0;
                        text-align: left !important;
                    }

                    .meter-table .meter-row > td[data-label]::before {
                        content: attr(data-label);
                        display: block;
                        margin-bottom: 2px;
                        font-size: 11px;
                        color: var(--text-secondary-light);
                    }

                    .meter-table .meter-row input {
                        width: 100% !important;
                        margin: 0 !important;
                    }

                    .invoice-setup-page .meter-table .meter-row > .meter-cell-index {
                        display: none;
                    }

                    .meter-table .meter-row > .meter-cell-flat {
                        order: -2;
                    }

                    .invoice-setup-page .meter-table .meter-row > .meter-cell-status {
                        order: -1;
                        align-self: center;
                        text-align: right !important;
                    }

                    .meter-table .meter-row > .meter-cell-action {
                        grid-column: 1 / -1;
                    }

                    .meter-table .meter-cell-action > div {
                        justify-content: flex-end !important;
                    }

                    .meter-table .meter-cell-action .btn {
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        width: 36px;
                        height: 36px;
                    }
                }
            `}</style>
        </div>
    );
};

export default MeterReading;
